import { useCallback, useEffect, useMemo } from 'react';
import { useImage } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import SmallLogoIcon from '@/assets/small-logo.svg';
import { UploadFileByQrLinkParamKey } from '@/common/constants';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { ICreateDocumentAggregatorDtoDocumentTypeEnum } from '@/dtos';
import { useOrganizationProperties } from '@/modules/transaction/hooks';
import { ReceiptQRCode } from '@/modules/UseMyMoney/queries';
import { USE_MY_MONEY_UPLOAD_STORE_KEY } from '@/modules/UseMyMoney/UseMyMoney.types';
import { useQRCodeStore } from '@/modules/UseMyMoney/widgets/QrCode/stores/useQRCode.store';

interface Props {
  qrCodeData?: ReceiptQRCode;
  isFetching?: boolean;
  onGenerateQrCode: () => void;
  documentType?: ICreateDocumentAggregatorDtoDocumentTypeEnum;
}

const useGenerateQrCode = ({ qrCodeData, isFetching, onGenerateQrCode, documentType }: Props) => {
  const { organizationProperties } = useOrganizationProperties();
  const { values: { documentId } } = useFilesInDocumentStore(USE_MY_MONEY_UPLOAD_STORE_KEY);
  const { isError: isLogoError } = useImage(organizationProperties?.minimizedLogoUrl);
  const handleSetQRCodeStore = useQRCodeStore((state) => state.setState);

  const fileUploadUrl = useMemo(() => {
    if (!qrCodeData?.fileUploadUrl) {
      return '';
    }
    const url = new URL(qrCodeData.fileUploadUrl);
    url.searchParams.set(UploadFileByQrLinkParamKey.DOCUMENT_ID, documentId);

    if (documentType) {
      url.searchParams.set(UploadFileByQrLinkParamKey.DOCUMENT_TYPE, documentType);
    }

    return url.toString();
  }, [qrCodeData?.fileUploadUrl, documentId, documentType]);

  const logoUrl = useMemo(() => {
    if (isLogoError || !organizationProperties?.minimizedLogoUrl) {
      return SmallLogoIcon;
    }

    return organizationProperties?.minimizedLogoUrl;
  }, [isLogoError, organizationProperties]);

  const validTo = useMemo(() => dayjs(qrCodeData?.validTo || 0).valueOf(), [qrCodeData]);
  const qrCodeId = useMemo(() => qrCodeData?.qrId, [qrCodeData]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    if (validTo) {
      const currentTime = dayjs().valueOf();
      const timeout = validTo - currentTime;

      if (timeout > 0) {
        timeoutId = setTimeout(() => {
          onGenerateQrCode();
        }, timeout);
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validTo]);

  const handleSetQrCodeId = useCallback(async () => {
    handleSetQRCodeStore({
      qrCodeId,
    });
  }, [handleSetQRCodeStore, qrCodeId]);

  useEffect(() => {
    if (!isFetching && qrCodeId) {
      handleSetQrCodeId();
    }
    // eslint-disable-next-line
  }, [isFetching, qrCodeId]);

  return {
    fileUploadUrl,
    logoUrl,
  };
};

export default useGenerateQrCode;
